import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers,
  FaEdit,
  FaSearch,
  FaQuestionCircle,
  FaTags,
  FaGift,
  FaCog,
  FaRocket,
  FaDatabase,
  FaUserShield,
} from 'react-icons/fa';
import { LuLayoutDashboard, LuUserCog, LuPanelLeft, LuFolderKanban, LuVideo, LuFileText, LuCalculator, LuBuilding2, LuBriefcaseBusiness, LuCalendarDays, LuFileSpreadsheet } from 'react-icons/lu';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';

const iconMap = {
  dashboard: LuLayoutDashboard,
  help: FaQuestionCircle,
  pricing: FaTags,
  'refer-and-earn': FaGift,
  onboarding: FaRocket,
  settings: FaCog,
  'account-settings': LuUserCog,
  'sidebar-header': LuPanelLeft,
  documents: LuFolderKanban,
  meetings: LuVideo,
  'tax-filing': LuFileText,
  bookkeeping: LuCalculator,
  'usa-company-formation': LuBuilding2,
  'cfo-advisory': LuBriefcaseBusiness,
  'registered-agent': FaUserShield,
  'annual-report': LuCalendarDays,
  'business-contract-templates': LuFileSpreadsheet,
};

const colorMap = {
  dashboard: 'from-blue-600 to-indigo-700',
  help: 'from-amber-500 to-orange-600',
  pricing: 'from-emerald-500 to-teal-600',
  'refer-and-earn': 'from-pink-500 to-rose-600',
  onboarding: 'from-violet-500 to-purple-600',
  settings: 'from-slate-600 to-gray-800',
  'account-settings': 'from-sky-500 to-blue-600',
  'sidebar-header': 'from-fuchsia-500 to-pink-600',
  documents: 'from-teal-500 to-emerald-600',
  meetings: 'from-violet-500 to-purple-600',
  'tax-filing': 'from-blue-500 to-cyan-600',
  bookkeeping: 'from-emerald-500 to-green-600',
  'usa-company-formation': 'from-blue-600 to-indigo-700',
  'cfo-advisory': 'from-violet-600 to-purple-700',
  'registered-agent': 'from-blue-500 to-cyan-600',
  'annual-report': 'from-indigo-500 to-purple-600',
  'business-contract-templates': 'from-blue-600 via-indigo-600 to-teal-500',
};

const CATEGORY_ORDER = ['Core', 'Support', 'General'];

const PageCard = ({ page, onEdit }) => {
  const Icon = iconMap[page.slug] || LuLayoutDashboard;
  const gradient = colorMap[page.slug] || 'from-blue-500 to-indigo-600';

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-card overflow-hidden flex flex-col">
      <div className={`h-2 bg-gradient-to-r ${gradient}`} />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-lg`}>
            <Icon className="w-5 h-5" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gray-100 dark:bg-dark-700 text-gray-500">
            {page.category}
          </span>
        </div>
        <h3 className="font-bold text-gray-900 dark:text-white mb-1">{page.label}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 flex-1">{page.description}</p>
        <div className="flex items-center justify-between gap-2">
          <span className={`text-xs font-medium ${page.editable ? 'text-emerald-600' : 'text-amber-600'}`}>
            {page.editable ? 'Editable' : 'Coming soon'}
          </span>
          {page.editable ? (
            <button
              type="button"
              onClick={() => onEdit(page.slug)}
              className="btn-premium text-sm py-2 px-4 flex items-center gap-2"
            >
              <FaEdit className="w-3.5 h-3.5" /> Edit Page
            </button>
          ) : (
            <button type="button" disabled className="px-4 py-2 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-400 text-sm cursor-not-allowed">
              Soon
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AllPortalPagesCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [pages, setPages] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/catalog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setPages(res.data.data || []);
    } catch {
      toastError('Failed to load portal pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);

  const handleSeed = async () => {
    if (!window.confirm('Seed all portal pages with preloaded content?')) return;
    setSeeding(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toastSuccess(res.data.message || 'Portal pages seeded');
      fetchCatalog();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to seed portal pages');
    } finally {
      setSeeding(false);
    }
  };

  const handleEdit = (slug) => navigate(`/portal-cms/${slug}`);

  const filtered = pages.filter((page) =>
    page.label.toLowerCase().includes(search.toLowerCase())
    || page.slug.toLowerCase().includes(search.toLowerCase())
    || page.category.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    pages: filtered.filter((page) => page.category === category),
  })).filter((group) => group.pages.length > 0);

  return (
    <PageLayout>
      <PageHeader
        title="Portal CMS"
        description="Manage User Portal page content — titles, sections, files, and preloaded defaults."
        icon={FaUsers}
        actions={
          <button
            type="button"
            onClick={handleSeed}
            disabled={seeding}
            className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
          >
            <FaDatabase className="w-3.5 h-3.5" />
            {seeding ? 'Seeding...' : 'Seed Preloaded Content'}
          </button>
        }
      />

      <div className="mb-6 p-5 rounded-2xl bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 border border-primary-100 dark:border-primary-800/40">
        <h2 className="text-base font-bold text-gray-900 dark:text-white mb-1">User Portal Content</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Each page ships with preloaded titles, subtitles, and icons. Account Settings opens a hub with three sub-pages to edit.
        </p>
      </div>

      <div className="relative mb-6">
        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          className="input-modern pl-11"
          placeholder="Search portal pages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {grouped.map(({ category, pages: categoryPages }) => (
        <section key={category} className="mb-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {categoryPages.map((page) => (
              <PageCard key={page.slug} page={page} onEdit={handleEdit} />
            ))}
          </div>
        </section>
      ))}

      {!loading && filtered.length === 0 && (
        <p className="text-center text-gray-500 py-12">No portal pages match your search.</p>
      )}
    </PageLayout>
  );
};

export default AllPortalPagesCMS;
